import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getJobList, getMoleculeStats, getReactionStats, getCurrentUser } from "../../utils/Api";
import { useAuth } from "../../utils/AuthContext";
import Config from "../../Config";
import { useUpload } from "../../contexts/UploadContext";
import DashboardSkeleton from "../../components/SkeletonLoading/DashboardSkeleton";
import {
  FileText,
  FlaskConical,
  ArrowRight,
  BookMarked,
  Loader2,
  CheckCircle2,
  X,
  Info,
  Atom
} from "lucide-react";
import StatCard from "./components/StateCard";

import FreePlanUpgradeModal from "../../modals/FreePlanUpgradeModal";
import Tag from "../../components/Tag";

type DocumentItem = Record<string, any>;

interface DashboardStats {
  totalDocuments: number;
  totalPages: number;
  totalMolecules: number;
  totalReactions: number;
}

const Dashboard: React.FC = () => {
  const {
    user,
    refreshSavedTime,
    needsPayment,
    accountType,
  } = useAuth();
  const { uploads } = useUpload();
  const navigate = useNavigate();
  const [documentList, setDocumentList] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalDocuments: 0,
    totalPages: 0,
    totalMolecules: 0,
    totalReactions: 0,
  });
const [showUpgradeBanner, setShowUpgradeBanner] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showUsageWarningBanner, setShowUsageWarningBanner] = useState(false);
  const [usageResetDays, setUsageResetDays] = useState<number | null>(null);
  const usagePeriodEndRef = React.useRef<number | null>(null);

  useEffect(() => {
    if (
      needsPayment &&
      localStorage.getItem("upgrade_banner_dismissed") !== "true"
    ) {
      setShowUpgradeBanner(true);
    }
  }, [needsPayment]);

  useEffect(() => {
    const loadUsageWarning = async () => {
      try {
        const [userRes, billingRes] = await Promise.all([
          getCurrentUser(),
          fetch(`${Config.API_URL}/api/billing/status`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
          }),
        ]);

        const { monthly_pages_uploaded, monthly_limit, account_type } = userRes.data;
        if (account_type === "free" || !monthly_limit) return;

        const usageRatio = monthly_pages_uploaded / monthly_limit;
        if (usageRatio < 0.75) return;

        let periodEnd: number | null = null;
        if (billingRes.ok) {
          const billing = await billingRes.json();
          periodEnd = billing.current_period_end ?? null;
        }

        const dismissedPeriod = localStorage.getItem("usage_75_dismissed_period");
        if (dismissedPeriod && periodEnd && dismissedPeriod === String(periodEnd)) return;

        if (periodEnd) {
          usagePeriodEndRef.current = periodEnd;
          const daysLeft = Math.ceil((periodEnd * 1000 - Date.now()) / (1000 * 60 * 60 * 24));
          setUsageResetDays(daysLeft > 0 ? daysLeft : 0);
        }
        setShowUsageWarningBanner(true);
      } catch {
        // silently ignore
      }
    };
    loadUsageWarning();
  }, []);
  const [justCompletedIds, setJustCompletedIds] = useState<Set<string>>(
    new Set(),
  );
  const completionPollRef = React.useRef<NodeJS.Timeout | null>(null);

  const fetchDocuments = async (
    background = false,
  ): Promise<DocumentItem[]> => {
    try {
      if (!background) setLoading(true);
      const { data } = await getJobList({ limit: 3, sort_order: "desc" });

      let docs: DocumentItem[] = [];
      if (data.documents && typeof data.documents === "object") {
        docs = Object.entries(data.documents).map(
          ([id, docData]: [string, any]) => ({
            ...docData,
            id,
          }),
        );
      }

      const personalDocs = docs.filter(
        (doc) =>
          !doc.organisation_id ||
          doc.user_id === user?.uid ||
          doc.is_shared_with_me,
      );

      setDocumentList(personalDocs);
      setStats((prevStats) => ({
        ...prevStats,
        totalDocuments: data.total_documents ?? 0,
        totalPages: data.total_page_count ?? 0,
      }));
      setError(null);
      return personalDocs;
    } catch (err: any) {
      setError(err.message ?? "Error fetching documents");
      setDocumentList([]);
      return [];
    } finally {
      if (!background) setLoading(false);
    }
  };

  const pollUntilTimestamped = React.useCallback(
    (completedIds: string[], attemptsLeft = 5) => {
      if (completionPollRef.current) {
        clearTimeout(completionPollRef.current);
      }
      completionPollRef.current = setTimeout(async () => {
        const docs = await fetchDocuments(true);
        const allTimestamped = completedIds.every((id) =>
          docs.some((d) => d.id === id && d.created_at),
        );
        if (!allTimestamped && attemptsLeft > 1) {
          pollUntilTimestamped(completedIds, attemptsLeft - 1);
        }
      }, 1000);
    },
    [],
  );

  const fetchMoleculeStats = async () => {
    try {
      const { data } = await getMoleculeStats();
      setStats((prev) => ({ ...prev, totalMolecules: data.total_molecules ?? 0 }));
    } catch {
      // leave at 0 on error
    }
  };

  const fetchReactionStats = async () => {
    try {
      const { data } = await getReactionStats();
      setStats((prev) => ({ ...prev, totalReactions: data.total_reactions ?? 0 }));
    } catch {
      // leave at 0 on error
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchMoleculeStats();
    fetchReactionStats();
  }, []);

  const prevUploadsRef = React.useRef(uploads);
  useEffect(() => {
    const prev = prevUploadsRef.current;
    const newlyCompleted = uploads.filter(
      (u) =>
        u.status === "complete" &&
        u.documentId &&
        prev.find((p) => p.id === u.id)?.status !== "complete",
    );
    if (newlyCompleted.length > 0) {
      const completedIds = newlyCompleted.map((u) => u.documentId as string);

      pollUntilTimestamped(completedIds);
      setTimeout(refreshSavedTime, 1000);
      setTimeout(fetchMoleculeStats, 1000);
      setTimeout(fetchReactionStats, 1000);
      setJustCompletedIds((prev) => {
        const next = new Set(prev);
        newlyCompleted.forEach((u) => next.add(u.documentId!));
        return next;
      });
    }
    prevUploadsRef.current = uploads;
  }, [uploads]);

  const getDocumentUploadStatus = (docId: string): string | null => {
    const upload = uploads.find(
      (u) =>
        u.documentId === docId &&
        u.status !== "complete" &&
        u.status !== "error",
    );
    return upload ? upload.status : null;
  };

  // Build placeholder rows for uploads that are active and not yet in the fetched list
  const activeUploadPlaceholders: DocumentItem[] = uploads
    .filter(
      (u) =>
        u.documentId &&
        u.status !== "complete" &&
        u.status !== "error" &&
        !documentList.some((doc) => doc.id === u.documentId),
    )
    .map((u) => ({
      id: u.documentId as string,
      name: u.documentName,
      page_count: 0,
      tags: [],
      created_at: null,
      _isUploadPlaceholder: true,
    }));

  const recentDocuments = [...activeUploadPlaceholders, ...documentList].slice(
    0,
    3 + activeUploadPlaceholders.length,
  );

  return (
    <div className="bg-white h-full flex flex-col overflow-hidden">
      <div className="w-full mx-auto pt-8 sm:pt-12 px-4 sm:px-6 lg:px-10 flex-shrink-0">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-semibold tracking-tight text-gray-900">Dashboard</h3>
            <p className="text-base text-gray-500 mt-0.5">Overview of your document workspace</p>
          </div>
          {accountType === "free" && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm text-gray-400">Free plan</span>
              <button
                onClick={() => setShowUpgradeModal(true)}
                title="Click to find out how you could benefit from Data Revival Individual features"
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <Info size={15} className="text-gray-400" />
              </button>
              <button
                onClick={() => navigate("/upgrade")}
                className="text-sm font-medium bg-[#1d365d] hover:bg-[#16294a] text-white px-3 py-1.5 rounded-lg transition-colors"
              >
                Upgrade
              </button>
            </div>
          )}
        </div>

        {showUsageWarningBanner && (
          <div className="mb-6 flex items-center justify-between gap-4 bg-yellow-50 border border-yellow-200 text-yellow-900 px-4 py-3 rounded-lg">
            <p className="text-sm">
              You have used 75% of your monthly limit
              {usageResetDays !== null && (
                <> · resets in {usageResetDays} {usageResetDays === 1 ? "day" : "days"}</>
              )}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => navigate("/settings")}
                className="text-sm font-medium text-yellow-900 underline underline-offset-2 hover:text-yellow-700 transition-colors"
              >
                View usage
              </button>
              <button
                onClick={() => {
                  const pe = usagePeriodEndRef.current;
                  localStorage.setItem("usage_75_dismissed_period", pe ? String(pe) : String(Date.now()));
                  setShowUsageWarningBanner(false);
                }}
                className="p-1 hover:bg-yellow-100 rounded transition-colors"
                aria-label="Dismiss"
              >
                <X size={15} />
              </button>
            </div>
          </div>
        )}

        {showUpgradeBanner && (
          <div className="mb-6 flex items-center justify-between gap-4 bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg">
            <p className="text-sm">
              There was an issue processing your payment, so your account is now on the Free plan.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => navigate("/upgrade")}
                className="text-sm font-medium bg-[#1d365d] hover:bg-[#16294a] text-white px-3 py-1.5 rounded-lg transition-colors"
              >
                Complete upgrade
              </button>
              <button
                onClick={() => {
                  localStorage.setItem("upgrade_banner_dismissed", "true");
                  setShowUpgradeBanner(false);
                }}
                className="p-1 hover:bg-amber-100 rounded transition-colors"
                aria-label="Dismiss"
              >
                <X size={15} />
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
            {error}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10">
        {loading ? (
          <DashboardSkeleton />
        ) : (
          <>
            {/* statistics cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard
                icon={<BookMarked size={20} />}
                title="Total Documents"
                value={stats.totalDocuments}
                iconBgColor="bg-blue-50"
                iconColor="text-blue-600"
              />
              <StatCard
                icon={<FileText size={20} />}
                title="Total Pages"
                value={stats.totalPages}
                iconBgColor="bg-green-50"
                iconColor="text-green-600"
              />
              <StatCard
                icon={<Atom size={20} />}
                title="Total Molecules"
                value={stats.totalMolecules}
                iconBgColor="bg-purple-50"
                iconColor="text-purple-600"
              />
              <StatCard
                icon={<FlaskConical size={20} />}
                title="Total Reactions"
                value={stats.totalReactions}
                iconBgColor="bg-orange-50"
                iconColor="text-orange-600"
              />
            </div>

            {/* recent documents */}
            <div className="border border-gray-200 rounded-lg mb-12">
              <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <h4 className="text-base font-semibold tracking-tight text-gray-900">
                  Recently created documents
                </h4>
                <button
                  onClick={() => navigate("/home/documents")}
                  className="text-sm font-medium text-[#1d365d] hover:text-[#2d4a75] flex items-center gap-1 transition-colors"
                >
                  View all
                  <ArrowRight size={14} />
                </button>
              </div>
              <div className="divide-y divide-gray-100">
                {recentDocuments.length === 0 ? (
                  <div className="px-5 py-14 text-center">
                    <FileText size={36} className="mx-auto mb-3 text-gray-300" />
                    <p className="text-sm font-medium text-gray-600 mb-1">No documents yet</p>
                    <p className="text-sm text-gray-400">Upload your first document to get started</p>
                  </div>
                ) : (
                  recentDocuments.map((doc) => {
                    const uploadStatus = getDocumentUploadStatus(doc.id);
                    const uploading = uploadStatus !== null;
                    return (
                      <div
                        key={doc.id}
                        className={`group px-5 py-4 transition-colors duration-100 flex items-start sm:items-center justify-between gap-3 ${
                          uploading
                            ? "opacity-60 cursor-default"
                            : "hover:bg-gray-50/50 cursor-pointer"
                        }`}
                        onClick={() => {
                          if (uploading) return;
                          setJustCompletedIds((prev) => {
                            const next = new Set(prev);
                            next.delete(doc.id);
                            return next;
                          });
                          navigate(`/view/${doc.id}`);
                        }}
                      >
                        <div className="flex-1 min-w-0">
                          {doc.organisation_name && (
                            <span className="text-xs text-gray-400 block mb-0.5">
                              {doc.organisation_name}
                            </span>
                          )}
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h5 className={`font-medium truncate ${uploading ? "text-gray-400" : "text-gray-900"}`}>
                              {doc.name || "Untitled Document"}
                            </h5>
                            {uploading && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded">
                                <Loader2 size={10} className="animate-spin" />
                                {uploadStatus === "processing" ? "Processing" : "Uploading"}
                              </span>
                            )}
                            {!uploading && justCompletedIds.has(doc.id) && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded">
                                <CheckCircle2 size={10} />
                                Ready
                              </span>
                            )}
                          </div>
                          {doc.tags && doc.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-1.5">
                              {doc.tags.map((tag: any) => (
                                <Tag key={tag.id} id={tag.id} name={tag.name} color={tag.color} />
                              ))}
                            </div>
                          )}
                          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400">
                            <span className="flex items-center gap-1">
                              <FileText size={13} />
                              {doc.page_count || 0} pages
                            </span>
                            <span>
                              {doc.created_at
                                ? new Date(doc.created_at).toLocaleString("en-GB", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: false,
                                  })
                                : "Just now"}
                            </span>
                          </div>
                        </div>
                        <ArrowRight
                          size={16}
                          className="text-gray-300 group-hover:text-gray-400 flex-shrink-0 mt-1 sm:mt-0 transition-colors"
                        />
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <FreePlanUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </div>
  );
};

export default Dashboard;
